import {
  Box,
  IconButton,
  Popover,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from "material-ui-popup-state/hooks";
import * as React from 'react';
import { TeamDetailsSnapShotIcon } from '../../containers/Home/icon/TeamDetailsSnapShotIcon';
import { activityAtom } from '../../stores/jotai/activities/activities';
import { baseAtom, getDisplayableActivityFeed } from '../../stores/jotai/base';
import MaterialTable from '../general/material-table';
import TeamInfoPopoverContext from './contexts/TeamInfoPopoverContext';
import OverviewInfoPopover from './overview-info-popover';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`team-details-tabpanel-${index}`}
      aria-labelledby={`team-details-tab-${index}`}
      sx={{
        padding: "10px"
      }}
      {...other}
    >
      {value === index && children}
    </Box>
  );
}

export default function MaterialInfoPopover(props) {
  const [base, setBase] = useAtom(baseAtom);
  const [activities, setActivities] = useAtom(activityAtom);

  const theme = useTheme();
  const popupState = usePopupState({
    variant: "popover",
    popupId: "statsPopover"
  });

  const activityPopupState = usePopupState({
    variant: "popover",
    popupId: "activityPopover"
  });

  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const teamInfoPopoverButtonRef = React.useRef(null);

  const { openTeamInfoPopover, closeTeamInfoPopover } = React.useContext(
    TeamInfoPopoverContext
  );

  const renderActivityFeed = () => {
    const source = getDisplayableActivityFeed(activities);

    let activityFeed = [];

    for (let i = 0; i < source?.length; i++) {
      activityFeed.push({
        ...activities[i],
        ...source[i].display
      });
    }

    return (
      <MaterialTable
        tableName="Activity Feed"
        tableRows={activityFeed}
        headCells={[
          {
            id: "chip",
            align: "left",
            label: ""
          },
          {
            id: "title",
            align: "left",
            label: "Activity"
          },
          {
            id: "subtext",
            align: "left",
            label: "Info"
          },
          {
            id: "date",
            align: "left",
            label: "Date"
          }
        ]}
        uniqueCol={"activityID"}
        disablePagination
        minimal={false}
        onSelectRow={(e) => {
          // TODO: implement navigation to each activity item
        }}
        selectedRow={undefined}
      />
    );
  };

  const renderActivityFeedPopover = () => {
    return (
      <Popover
        {...bindPopover(activityPopupState)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        sx={{
          "&.MuiPaper-root": {
            boxShadow: `${theme?.custom?.menuBoxShadow} !important`
          }
        }}
      >
        <Box
          sx={{ borderBottom: 1, borderColor: "divider", background: "white" }}
        >
          <Tabs
            sx={{ marginBottom: "-1.5px" }}
            value={value}
            onChange={handleChange}
            aria-label="scope of the activity you are viewing"
          >
            <Tab label="Team" />
            <Tab label="Yours" />
          </Tabs>
        </Box>
        <TabPanel value={value} index={0}>
          <Box sx={{ background: "white", margin: "0px" }}>
            {renderActivityFeed()}
          </Box>
        </TabPanel>
        <TabPanel value={value} index={1}>
          <Box sx={{ background: "white", margin: "0px" }}>
            {renderActivityFeed()}
          </Box>
        </TabPanel>
      </Popover>
    );
  };

  const handleOpenTeamInfoPopover = React.useCallback(
    (params = {}) => {
      const { activeTabIndex = 0 } = params;

      setValue(activeTabIndex);
      popupState.open(teamInfoPopoverButtonRef.current);
    },
    [popupState, teamInfoPopoverButtonRef.current]
  );

  const handleCloseTeamInfoPopover = React.useCallback(() => {
    popupState.close();
  }, [popupState]);

  React.useEffect(() => {
    if (teamInfoPopoverButtonRef) {
      openTeamInfoPopover.current = handleOpenTeamInfoPopover;
      closeTeamInfoPopover.current = handleCloseTeamInfoPopover;
    }
  }, [
    teamInfoPopoverButtonRef,
    handleOpenTeamInfoPopover,
    handleCloseTeamInfoPopover
  ]);

  return (
    <div>
      <Tooltip title="Team Details Snapshot">
        <IconButton
          ref={teamInfoPopoverButtonRef}
          {...bindTrigger(popupState)}
          sx={{
            ":hover": {
              color: `#1976D2 !important`
            }
          }}
        >
          <TeamDetailsSnapShotIcon
            sx={{
              width: "23px",
              height: "23px",
              color: "inherit",
              border: "1px solid transparent"
            }}
          />
        </IconButton>
      </Tooltip>
      {renderActivityFeedPopover()}
      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        sx={{
          ".MuiPaper-root": {
            margin: "0 0 0 8px",
            boxShadow: `${theme?.custom?.menuBoxShadow} !important`
          }
        }}
      >
        <OverviewInfoPopover
          team={props?.team}
          cohort={props?.cohort}
          base={base}
          value={value}
          handleChange={handleChange}
        />
      </Popover>
    </div>
  );
}
